<script>
  import { onMount } from 'svelte';

  import { ComponentType, TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION } from '../../../../../../js/constants';
  import { getNavState, categoryOpenStates, flagsStore } from '../../../../../../js/stores';
  import { hoverWithDelay } from '../../../../../../js/hoverWithDelay';
  import { onClickOutside } from '../../../../../../js/onClickOutside';
  import { onTouchOrClick } from '../../../../../../js/onTouchOrClick';
  import { onIntersect } from '../../../../../../js/onIntersect';
  import { dispatchOffsetLeftOnResize } from '../../../../../../js/dispatchOffsetLeftOnResize';
  import { fetchPlacementMessages, mapMessagesToSpotlightItems } from '../../../../../../js/commsPlatUtils';
  import { getCategory, getSubcategory, getNavDeviceType } from '../../../../../../js/trackingUtils';
  import { trackCdp } from '../../../../../../js/tracking';

  import CategoryListFlattened from '../../../DesktopNavFlattened/CategoryFlattened/CategoryListFlattened/CategoryListFlattened.svelte';
  import FlyoutFlattened from '../../../DesktopNavFlattened/FlyoutFlattened/FlyoutFlattened.svelte';

  // Props
  export let id = undefined;
  export let title = undefined;
  export let navItems = [];
  export let spotlightConfig = undefined;
  export let isVisible = false;

  // Constants used in flattened nav
  const navStateStore = getNavState();
  const trackData = { menuId: id, menuText: title };

  // Variables
  let element;
  let isOpen = false;
  let offsetLeft;

  let defaultSpotlightConfig;
  let fetchPlacementMessagesAttempted = false;
  let fetchPlacementMessagesSucceeded = false;
  let viewedCommsPlatMessages = false;

  // Reactive declarations used in flattened nav
  $: isCurrentCategory = $navStateStore.category === element;

  $: if (
    $flagsStore.get('publicwebnav/enable-comms-plat-messaging') &&
    typeof window !== 'undefined' &&
    spotlightConfig &&
    !fetchPlacementMessagesAttempted
  ) {
    defaultSpotlightConfig = spotlightConfig;
    spotlightConfig = {
      items: [],
      title: defaultSpotlightConfig.title,
      communicationPlatformPlacementIds: defaultSpotlightConfig.communicationPlatformPlacementIds,
    };
    // eslint-disable-next-line unicorn/prefer-top-level-await
    getSpotlightMessages(defaultSpotlightConfig);
  }

  export async function getSpotlightMessages(unformattedSpotlightConfig) {
    // Public Web Products #1, Public Web Products #2, Public Web Business Types Nav #1, Public Web Business Types Nav #2
    const placementIds = (unformattedSpotlightConfig.communicationPlatformPlacementIds || []).slice(0, 2);
    if (placementIds.length > 0) {
      try {
        const placementMessages = await fetchPlacementMessages(placementIds);
        // map default and placement messages
        const formattedSpotlightItems =
          placementMessages.length > 0
            ? mapMessagesToSpotlightItems(placementMessages)
            : unformattedSpotlightConfig.items;
        spotlightConfig.items = formattedSpotlightItems;
        fetchPlacementMessagesSucceeded = true;
      } catch {
        trackCdp({
          eventName: 'Error fetching placement messages',
          properties: {
            ...trackData,
          },
        });
        spotlightConfig.items = defaultSpotlightConfig.items;
      }
    } else {
      spotlightConfig.items = defaultSpotlightConfig.items;
    }

    fetchPlacementMessagesAttempted = true;
  }

  function trackViewCommsPlatMessage(spotlightConfigItems) {
    // track view message event for each message
    spotlightConfigItems.forEach((item) => {
      if (item.requestToken) {
        trackCdp({
          eventName: 'View Commsplat Message',
          properties: {
            ...trackData,
            requestToken: item.requestToken,
            messageId: item.messageId,
            placementId: item.placementId,
          },
        });
      }
    });
  }

  // Open the flyout
  export function open() {
    if (isOpen) {
      return;
    }
    isOpen = true;
    categoryOpenStates.update((openStates) => openStates.set(id, isOpen));
    $navStateStore = {
      ...$navStateStore,
      category: element, // Set current category
      subcategory: null, // Clear current subcategory
    };
    trackCdp({
      eventName: 'Open Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        menuLocation: ComponentType.SUBNAVIGATION,
        navDeviceType: getNavDeviceType(element),
      },
    });

    // Only track comms plat message view feature flag enabled and
    // if it's the first time opening the nav to view the messages
    // and if the messages being viewed are from comms plat (not default)
    if (
      $flagsStore.get('publicwebnav/enable-comms-plat-messaging') &&
      !viewedCommsPlatMessages &&
      fetchPlacementMessagesSucceeded &&
      (spotlightConfig || {}).items
    ) {
      viewedCommsPlatMessages = true;
      trackViewCommsPlatMessage(spotlightConfig.items);
    }
  }

  // Close the flyout
  function close() {
    if (!isOpen) {
      return;
    }
    isOpen = false;
    categoryOpenStates.update((openStates) => openStates.set(id, isOpen));
    trackCdp({
      eventName: 'Close Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        menuLocation: ComponentType.SUBNAVIGATION,
        navDeviceType: getNavDeviceType(element),
      },
    });
  }

  // Toggle the flyout
  function toggle() {
    isOpen ? close() : open();
  }

  // Ensure only one flyout is open at a time.
  $: if (isOpen && !isCurrentCategory) {
    close();
  }

  onMount(() => () => close());
</script>

<div
  {id}
  class="CategoryFlattened"
  class:isOpen
  class:isVisible
  bind:this={element}
  data-category={title}
  data-component-type={ComponentType.SUBNAVIGATION_MENU}
  use:hoverWithDelay={[open, close, TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION]}
  use:onClickOutside={close}
  use:dispatchOffsetLeftOnResize
  use:onIntersect={() => {
    offsetLeft = element.offsetLeft;
  }}
  on:offsetLeftOnResize={({ detail }) => {
    offsetLeft = detail;
  }}
>
  <button
    class="toggle nav-button"
    class:isOpen
    aria-haspopup="true"
    aria-expanded={isOpen ? true : false}
    use:onTouchOrClick={toggle}
  >
    <span class="title">{title}</span>
    <div class="chevron-container">
      <svg class="chevron-left" width="5" height="5" viewBox="0 0 5 5" fill="none">
        <path d="M4 1L1 4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <svg class="chevron-right" width="5" height="5" viewBox="0 0 5 5" fill="none">
        <path d="M4 1L1 4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </div>
  </button>
  <FlyoutFlattened {isOpen} flyoutOffsetLeft={offsetLeft}>
    <div class="category-flattened-container" class:isOpen>
      <CategoryListFlattened {navItems} {spotlightConfig} />
    </div>
  </FlyoutFlattened>
</div>

<style>@charset "UTF-8";
.CategoryFlattened {
  box-sizing: border-box;
  z-index: 1;
  position: relative;
  display: none;
  margin: 0;
  padding: 0;
}
.CategoryFlattened.isOpen {
  z-index: 2;
}
.CategoryFlattened.isVisible {
  display: inline-block;
}

.category-flattened-container {
  opacity: 0;
  transition: transform 150ms, opacity 150ms;
  transform-origin: top center;
  transform: translateY(-10px);
  display: flex;
  padding: 0 clamp(0px, calc((var(--nav-width) - var(--desktop-breakpoint)) / 2), calc(var(--subtitle-offset-left) - 24px));
  width: calc(var(--nav-width));
  max-width: 100%;
}
.category-flattened-container.isOpen {
  opacity: 1;
  transform: translateY(0);
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms linear;
  transition-delay: 150ms;
}

.toggle.nav-button {
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, #1a1a1a);
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: var(--font-weight-medium, 500);
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  background-image: linear-gradient(var(--color-text), var(--color-text));
  background-size: 0% 2px;
  background-repeat: no-repeat;
  background-position: calc(100% - 0px) 100%;
  transition: background-size 400ms cubic-bezier(0.4, 0, 0.2, 1);
  padding: 21px 0;
}
.toggle.nav-button:focus {
  outline: none;
}
.toggle.nav-button:focus-visible:not(:active) {
  outline-offset: 2px;
  outline: 2px auto Highlight;
  outline: 2px auto -webkit-focus-ring-color;
}
.toggle.nav-button.isOpen {
  background-position: calc(0% + 0px) 100%;
  background-size: calc(100% - 0px) 2px;
}
.toggle.nav-button .chevron-container {
  display: inline-block;
  width: 9px;
  margin-left: 3px;
}
.toggle.nav-button .chevron-container .chevron-left,
.toggle.nav-button .chevron-container .chevron-right {
  display: inline-block;
  max-width: none;
  stroke: var(--color-text);
  transition: transform 400ms cubic-bezier(0.4, 0, 0.2, 1);
}
.toggle.nav-button .chevron-container .chevron-left {
  transform: rotate(90deg);
}
.toggle.nav-button .chevron-container .chevron-right {
  transform: translateX(-6px);
}
.toggle.nav-button.isOpen .chevron-container .chevron-left {
  transform: rotateX(0deg);
}
.toggle.nav-button.isOpen .chevron-container .chevron-right {
  transform: rotate(90deg) translateX(0) translateY(6px);
}</style>
